import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import useDebounce from '../../hooks/useDebounce';
import { get_user_info, searchUsersRequest } from '../../helpers/api';
import styles from '../pages/Search/styles.module.scss';
import CloseSharpIcon from '@material-ui/icons/CloseSharp';
import List from '@material-ui/core/List';
import Loader from '../common/Loader';
import ShowResultSearch from '../common/ShowResultSearch';
import { toast } from 'react-toastify';
import RoundedChip from '../common/RoundedChip';
import { useInfiniteQuery } from '@tanstack/react-query';
import { IconButton, TextField } from '@mui/material';
import Button from '@mui/material/Button';
import { useTranslation } from '../../i18n/LanguageProvider';

const UserSearchDialog = (props) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 1000);
  const [searchCompleted, setSearchCompleted] = useState(false);
  const [results, setResults] = useState(props.currentUsers || []);

  const listRef = useRef(null);

  const { isLoading, data, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useInfiniteQuery(
      ['users', searchTerm],
      ({ pageParam = 1 }) => searchUsersRequest(pageParam, searchTerm),
      {
        select: React.useCallback(
          (data) => data?.pages.flatMap((user) => user.data),
          []
        ),
        enabled:
          (debouncedSearchTerm && searchTerm.length >= 2) || searchTerm === '',
        getNextPageParam: (lastPage, allPages) => {
          const nextPage = allPages.length + 1;
          return lastPage.data.length !== 0 ? nextPage : undefined;
        },
        onError(err) {
          toast.error(err.response?.data?.detail);
        }
      }
    );

  const addUser = (u) => {
    if (props.multi) {
      if (!results.filter((res) => res.id === u.id).length) {
        setResults([...results, u]);
      } else {
        setResults(results.filter((r) => r.id !== u.id));
      }
    } else {
      props.selectUsers(u)();
    }
  };

  const handleDeleteUser = (u) => {
    setResults(results.filter((item) => item.id !== u.id));
  };

  useEffect(() => {
    props.selectUsers(results);
  }, [results]);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [results.length]);

  return (
    <div className={styles.wrapper} style={{ height: '50vh', marginTop: 16 }}>
      <div>
        <div
          style={{ marginBottom: 16 }}
          className={styles.optionsList}
          ref={listRef}
        >
          {results.map((result, index) => (
            <RoundedChip
              key={index}
              name={result.name}
              onDelete={() => handleDeleteUser(result)}
              avatar={result.img}
            />
          ))}
        </div>
      </div>
      <TextField
        id="search"
        name="search"
        placeholder={t('search.placeholder')}
        hiddenLabel
        InputProps={{
          endAdornment:
            searchTerm !== '' ? (
              <IconButton onClick={() => setSearchTerm('')}>
                <CloseSharpIcon />
              </IconButton>
            ) : null,
          disableUnderline: true
        }}
        onChange={(e) => setSearchTerm(e.target.value)}
        onActionIconClick={() => {
          setSearchTerm('');
          setSearchCompleted(false);
        }}
        value={searchTerm}
      />
      <List className={styles.list}>
        {isLoading ? (
          <Loader size={40} />
        ) : data.length !== 0 ? (
          data
            .filter((u) => u.id !== get_user_info().id)
            .map((user, index) => (
              <ShowResultSearch
                key={index}
                selected={results.map((u) => u.id).includes(user.id)}
                customOnClick={() =>
                  addUser({
                    id: user.id,
                    name: `${user.first_name} ${user.last_name}`,
                    img: user.avatar?.thumbnail
                  })
                }
                user={user}
              />
            ))
        ) : searchCompleted ? (
          <div className={styles.empty}>{t('search.noResult')}</div>
        ) : null}
      </List>
      {!isLoading &&
        (isFetchingNextPage ? (
          <Loader size={40} />
        ) : (
          hasNextPage && (
            <Button fullWidth variant="outlined" onClick={fetchNextPage}>
              {t('common.more')}
            </Button>
          )
        ))}
    </div>
  );
};

UserSearchDialog.propTypes = {
  selectUsers: PropTypes.func,
  currentUsers: PropTypes.array,
  multi: PropTypes.bool
};

export default UserSearchDialog;
